import { getDateKey, getDateSeed, getNormalizedDate, getWordNumber } from './dateUtils';
import guessWords from '@/data/guessWords.json';
import secureAnswerSchedule from '@/data/secureAnswerSchedule.json';
import { bruteForceWord, hashLetter } from './cryptoUtils';

// Type assertions for JSON imports
export const guessWordsList = guessWords as string[];
export const secureAnswerScheduleMap = secureAnswerSchedule as Record<string, SecureTargetWord>;

// Get a random word from our answer list
export const getRandomWord = (): SecureTargetWord => {
  const answerWords = Object.values(secureAnswerScheduleMap);
  return answerWords[Math.floor(Math.random() * answerWords.length)];
};

// Get word of the day based on current date
export const getWordOfTheDay = (providedDate?: Date): SecureTargetWord => {
  const date = getNormalizedDate(providedDate);
  const dateKey = getDateKey(date);
  return secureAnswerSchedule[dateKey];
};

export const getHintLetter = (date: Date): string => {
  const targetWord = getWordOfTheDay(date);
  const dailyWord = bruteForceWord(targetWord);
  // Remove vowels from the daily word so that only a consonant is given as a hint
  const dailyWordNoVowels = dailyWord.replace(/[aeiou]/gi, '');
  const seed = getDateSeed(date);

  // Use the seed to pick a word deterministically
  const index = seed % dailyWordNoVowels.length;
  return dailyWordNoVowels[Math.abs(index)];
};

// Check if a word exists in our guess list
export const isValidWord = (word: string): boolean => {
  return guessWordsList.includes(word.toLowerCase());
};

// Type for evaluating a guess
export type LetterEvaluation = 'correct' | 'present' | 'absent';

// Type for a hashed target word
export type SecureTargetWord = {
  salt: string,
  hashedLetters: string[]
};

// Evaluate a guess against the target word
export const evaluateGuess = (guess: string, targetWord: SecureTargetWord): LetterEvaluation[] => {
  const result: LetterEvaluation[] = Array(targetWord.hashedLetters.length).fill('absent');

  // We need to make a copy of the target letters so we can modify
  // them locally
  const targetLetters = [...targetWord.hashedLetters];

  // First pass: mark correct letters
  for (let i = 0; i < guess.length; i++) {
    const targetLetterHash = targetLetters[i];
    const guessLetterHash = hashLetter(targetWord.salt, guess[i]);
    if (guessLetterHash === targetLetterHash) {
      result[i] = 'correct';
      targetLetters[i] = '#'; // Mark as used
    }
  }

  // Second pass: mark present letters
  for (let i = 0; i < guess.length; i++) {
    if (result[i] === 'absent') {
      const guessLetterHash = hashLetter(targetWord.salt, guess[i]);
      const index = targetLetters.indexOf(guessLetterHash);
      if (index !== -1) {
        result[i] = 'present';
        targetLetters[index] = '#'; // Mark as used
      }
    }
  }

  return result;
};

// Get all keyboard keys
export const KEYBOARD_KEYS = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['enter', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'backspace']
];
